/**
 * Tudo o que o mapa desenha, derivado de três fontes: a planilha (a etapa de
 * cada ambiente), o estado do app (setores cadastrados, subsetores, postos,
 * pessoas, alocação, tarefas, peças desmembradas) e os filtros. Só memo:
 * nenhuma escrita, nenhum efeito.
 *
 * Quem decide QUAIS salas existem é o `catalogo` (vem de fora, porque o modo
 * Organizar trabalha em cima dele); aqui ele só é consultado.
 */
import { useMemo } from "react";

import { combinarDemandas } from "./dados/combinar";
import useHoje from "./dados/useHoje";
import { aplicarFiltros } from "./dados/filtros";
import { combinarPecas } from "./dados/pecas";
import { tarefaAberta } from "./dados/vocabulario";
import { montarDestaque } from "./destaque";
import { gargaloDaFabrica, indicadoresDoMapa, pessoasNoEscopo, resumoDaSala } from "./indicadores";
import { situacaoDasPessoas } from "./pessoas";
import { montarPlanta } from "./planta";
import { SITUACAO, derivarDemandas, resumirMapa } from "./setores";

function contarTarefasPorPosto(tarefas) {
  const mapa = new Map();
  tarefas.forEach((t) => {
    if (t.posto_id && tarefaAberta(t)) mapa.set(t.posto_id, (mapa.get(t.posto_id) || 0) + 1);
  });
  return mapa;
}

// Partes da sala que o filtro pode acender (pessoas, bancadas, áreas).
function partesDaSala(sala, situacoes) {
  const bancadas = sala.areas.flatMap((a) => a.bancadas);
  const pessoas = bancadas.flatMap((b) => b.pessoas.map((p) => situacoes.get(p.colaboradorId)).filter(Boolean));
  return { pessoas, bancadas, areas: sala.areas };
}

function resumosPorSala(salas, combinadas, pecas, passa) {
  return new Map(salas.map((sala) => {
    const doSetor = combinadas.filter((d) => d.situacao === SITUACAO.SETOR && d.setorId === sala.setorId);
    const pecasDoSetor = pecas.filter((p) => p.setorId === sala.setorId);
    return [sala.setorId, resumoDaSala(doSetor, passa, pecasDoSetor)];
  }));
}

/**
 * useCenaDoMapa({dados, estado, catalogo, filtros, totalAtivos, elementos}) →
 * {combinadas, pecas, filtrados, destaque, situacoes, planta, resumos,
 *  salasApagadas, tarefasPorPosto, indicadores, gargalo, bandejas}
 */
export default function useCenaDoMapa({ dados, estado, catalogo, filtros, totalAtivos, elementos }) {
  // "Hoje" troca à meia-noite (`useHoje`) e a planilha é rederivada a cada
  // carga: atraso é calculado, nunca gravado.
  const hoje = useHoje();
  const demandasPlanilha = useMemo(() => derivarDemandas(dados, { catalogo, hoje }), [dados, catalogo, hoje]);
  const combinadas = useMemo(
    () => combinarDemandas(demandasPlanilha, estado, { catalogo, hoje }),
    [demandasPlanilha, estado, catalogo, hoje]
  );
  const pecas = useMemo(() => combinarPecas(estado, combinadas, { catalogo, hoje }), [estado, combinadas, catalogo, hoje]);
  // O mapa desenha só as peças que estão CIRCULANDO: retornada, concluída e
  // cancelada são história e entupiriam as salas para sempre. Elas continuam em
  // `pecas` (o popover do ambiente e os filtros as veem).
  const pecasNoMapa = useMemo(() => pecas.filter((peca) => peca.fora), [pecas]);
  const filtrados = useMemo(
    () => aplicarFiltros({ demandas: combinadas, tarefas: estado.tarefas, pecas }, filtros, { estado, hoje }),
    [combinadas, pecas, estado, filtros, hoje]
  );
  const ativos = totalAtivos > 0;
  const situacoes = useMemo(() => situacaoDasPessoas(estado, combinadas), [estado, combinadas]);
  const destaque = useMemo(() => montarDestaque({ filtros, filtrados, ativos }), [filtros, filtrados, ativos]);
  const planta = useMemo(
    () => montarPlanta(elementos, {
      combinadas,
      pecas: pecasNoMapa,
      estado,
      passa: destaque.passaAmbiente,
      passaPeca: destaque.passaPeca,
    }),
    [elementos, combinadas, pecasNoMapa, estado, destaque.passaAmbiente, destaque.passaPeca]
  );
  const resumos = useMemo(
    () => resumosPorSala(planta.salas, combinadas, pecasNoMapa, destaque.passaAmbiente),
    [planta.salas, combinadas, pecasNoMapa, destaque.passaAmbiente]
  );
  const salasApagadas = useMemo(
    () => new Set(
      planta.salas
        .filter((s) => destaque.salaApagada(s.setorId, partesDaSala(s, situacoes)))
        .map((s) => s.setorId)
    ),
    [planta.salas, destaque, situacoes]
  );
  const tarefasPorPosto = useMemo(() => contarTarefasPorPosto(filtrados.tarefas), [filtrados.tarefas]);
  const indicadores = useMemo(
    () => indicadoresDoMapa({
      demandas: filtrados.demandas,
      tarefas: filtrados.tarefas,
      pecas: filtrados.pecas,
      pessoas: pessoasNoEscopo(situacoes, filtros, estado),
      catalogo,
    }),
    [filtrados, situacoes, filtros, estado, catalogo]
  );
  // O gargalo marcado na sala segue o filtro, como a faixa.
  const gargalo = useMemo(
    () => gargaloDaFabrica(filtrados.demandas, catalogo)?.setorId || null,
    [filtrados.demandas, catalogo]
  );
  const bandejas = useMemo(() => resumirMapa(combinadas, catalogo), [combinadas, catalogo]);

  return {
    combinadas, pecas, filtrados, destaque, situacoes, planta, resumos,
    salasApagadas, tarefasPorPosto, indicadores, gargalo, bandejas, hoje,
  };
}
